import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import Constants from '../../utils/constants';
import { authHeader } from '../../utils/headers';

// Dérive la base API à partir d'une URL constante (même pattern que graffitiApi)
function getApiBase() {
    try {
        const u = new URL(
            Constants.API_ROUTE_CLIENT_CONTENEURS_GEOJSON.replace(':clientId', '0')
        );
        return `${u.protocol}//${u.host}`;
    } catch (e) {
        return import.meta.env.VITE_APIURL || '';
    }
}

export const conteneurMapApi = createApi({
    reducerPath: 'conteneurMapApi',
    baseQuery: fetchBaseQuery({
        baseUrl: getApiBase(),
        prepareHeaders: (headers) => {
            const token = authHeader();
            if (typeof token === 'string' && token) {
                headers.set('authorization', token);
            }
            // Convention RTK Query du repo : Accept ld+json forcé (le GeoJSON est un JsonResponse, insensible).
            headers.set('Accept', 'application/ld+json');
            return headers;
        },
    }),
    endpoints: (builder) => ({
        // GET /api/clients/{clientId}/conteneurs/geojson?patrimoineId=&statut=
        // Mode checklist (PAT-04 §5) : &mode=checklist&acte=CODE&dateDebut=YYYY-MM-DD&dateFin=YYYY-MM-DD
        // → chaque feature porte `properties.checklist`. La clé de cache RTK inclut ces args.
        getConteneursGeoJson: builder.query({
            query: ({ clientId, patrimoineId, statut, mode, acte, dateDebut, dateFin }) => {
                const base = Constants.API_ROUTE_CLIENT_CONTENEURS_GEOJSON
                    .replace(':clientId', clientId);
                const params = new URLSearchParams();
                if (patrimoineId) params.set('patrimoineId', String(patrimoineId));
                if (statut) params.set('statut', String(statut));
                if (mode === 'checklist' && acte) {
                    params.set('mode', 'checklist');
                    params.set('acte', String(acte));
                    if (dateDebut) params.set('dateDebut', dateDebut);
                    if (dateFin) params.set('dateFin', dateFin);
                }
                const qs = params.toString();
                return qs ? `${base}?${qs}` : base;
            },
            // Normalise la réponse en FeatureCollection valide
            transformResponse: (response) => {
                if (response && Array.isArray(response.features)) return response;
                return { type: 'FeatureCollection', features: [] };
            },
        }),
        // Actes de nature CHECKLIST bindés aux formulaires du client (PAT-04 R11, option « formulaires du
        // client » tranchée le 07/10). GET /api/forms (non paginé) filtré côté front : pas de filtre
        // `client` déclaré sur la ressource. Dédoublonné par code, éléments (dictionnaire) conservés.
        getClientChecklistActes: builder.query({
            query: () => `${Constants.API_ROUTE_FORMS}`,
            transformResponse: (response, _meta, clientId) => {
                const forms = Array.isArray(response) ? response
                    : (Array.isArray(response?.['hydra:member']) ? response['hydra:member'] : (response?.member ?? []));
                const wanted = String(clientId);
                const clientIdOf = (c) => {
                    if (c == null) return null;
                    if (typeof c === 'object') return c.id != null ? String(c.id) : String(c['@id'] ?? '').split('/').pop();
                    return String(c).split('/').pop();
                };
                const byCode = new Map();
                forms.forEach((f) => {
                    if (clientIdOf(f?.client) !== wanted) return;
                    (f?.formActes ?? []).forEach((fa) => {
                        const a = fa?.acte;
                        if (!a || a.nature !== 'CHECKLIST' || !a.code || byCode.has(a.code)) return;
                        byCode.set(a.code, {
                            code: a.code,
                            libelle: a.libelleFr ?? a.code,
                            picto: a.picto ?? null,
                            elements: (a.elements ?? []).map((e) => ({ code: e.code, libelle: e.libelleFr ?? e.code, ordre: e.ordre ?? 0 }))
                                .sort((x, y) => x.ordre - y.ordre),
                        });
                    });
                });
                return Array.from(byCode.values()).sort((x, y) => x.libelle.localeCompare(y.libelle));
            },
        }),
    }),
});

export const {
    useGetConteneursGeoJsonQuery,
    useLazyGetConteneursGeoJsonQuery,
    useGetClientChecklistActesQuery,
} = conteneurMapApi;
